import { useEffect, useState } from 'react'
import { type lookup, type postError } from 'core/types'
import { sendGet } from 'hooks/use-fetch'
import { type ChildRelationship } from 'Pages/CaregiverPortal/Members/ChildRelationships'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { Avatar, Box, Button, type SelectChangeEvent, Typography } from '@mui/material'
import { CheckBoxWithLabel } from 'Components/CheckBox'

export interface RelationshipEditProps {
  onCancel: () => void
  onSubmit: (relationship: ChildRelationship) => void
  onDelete?: () => void
  errors?: postError[]
  relationship: ChildRelationship
  childName: string
  candidates?: ChildRelationship[]
  onSwitchCandidate?: (candidate: ChildRelationship) => void
  // true when the candidates being picked are children (editing a caregiver); false when they are caregivers (editing a child)
  isChild: boolean
}

export function RelationshipEdit (props: RelationshipEditProps): JSX.Element {
  const [relationship, setRelationship] = useState<ChildRelationship>(props.relationship)
  const [relationshipTypes, setRelationshipTypes] = useState<lookup[]>([])
  const [showRelationValues, setShowRelationValues] = useState(props.relationship.relationshipID !== null)
  const isExisting = props.relationship.id != null && props.relationship.id > 0
  const isPicking = !isExisting && (props.candidates != null && props.candidates.length > 1)
  // blank until a child is picked when adding a relationship from the caregiver side
  const childName = props.childName !== '' ? props.childName : 'this child'

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: relations, error: err1 } = await sendGet('/Relationship/GetLookup', {})
      if (err1[0] === '') {
        setRelationshipTypes(relations)
      }
    }
    void fetchData()
  }, [])

  const handleTypeChange = async (e: SelectChangeEvent<number | null>): Promise<void> => {
    // MUI hands the option value back as a string; keep the id numeric and carry the name along, since the
    // RelationshipCard shown after Update displays relationshipName, not the id
    const value = e.target.value as unknown as string
    const relationshipID = value === '' ? null : Number(value)
    const relationshipName = relationshipTypes.find(t => t.id === relationshipID)?.name ?? null
    setRelationship({ ...relationship, relationshipID, relationshipName })
    setShowRelationValues(value !== '')
  }

  const handleValueChange = (name: string, newValue: boolean): void => {
    setRelationship({ ...relationship, [name]: newValue })
  }

  const handleSubmit = (): void => {
    props.onSubmit(relationship)
  }

  const handleCandidateChange = (e: SelectChangeEvent<number>): void => {
    const candidate = props.candidates?.find(c => c.caregiverID === Number(e.target.value))
    if (candidate != null) {
      // the parent remounts this editor for the picked candidate, so carry over what was already entered - otherwise
      // choosing the type before the person silently wipes it
      props.onSwitchCandidate?.({
        ...candidate,
        relationshipID: relationship.relationshipID,
        relationshipName: relationship.relationshipName,
        livesWithChild: relationship.livesWithChild,
        financiallyResponsibleFor: relationship.financiallyResponsibleFor
      })
    }
  }

  const buttonSx = { borderRadius: '24px', height: '52px', textTransform: 'none' as const, fontWeight: 600 }

  return (
    <Box sx={{ backgroundColor: '#ECEDEF', borderRadius: '10px', p: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {isPicking
        ? (
          <SelectWithLabel
            width='100%'
            label={props.isChild ? 'Child' : 'Caregiver'}
            name='caregiverID'
            includeNoneOption={false}
            onChange={handleCandidateChange}
            options={(props.candidates ?? []).map(c => ({ id: c.caregiverID, name: c.caregiverName }))}
            value={relationship.caregiverID === 0 ? undefined : relationship.caregiverID}
            SelectProps={{ sx: { backgroundColor: 'white' } }}
          />
          )
        : (
          <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Avatar sx={{ height: '32px', width: '32px' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#222024' }}>{relationship.caregiverName}</Typography>
          </Box>
          )}

      {props.errors !== undefined && props.errors.length > 0 &&
        <ul>
          {props.errors.map((err) => (
            <li key={err.field} style={{ color: 'red', padding: '10px 0 0' }}>
              {err.error}
            </li>
          ))}
        </ul>
      }

      <SelectWithLabel
        width='100%'
        label='Nature of Relationship'
        name='relationshipID'
        includeNoneOption={!isExisting}
        onChange={handleTypeChange}
        options={relationshipTypes}
        value={relationship.relationshipID ?? undefined}
        SelectProps={{ sx: { backgroundColor: 'white' } }}
      />

      {showRelationValues && (
        <Box sx={{ backgroundColor: 'white', borderRadius: '8px', p: '12px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <CheckBoxWithLabel label={`Lives with ${childName}`} edge='start' name='livesWithChild'
            value={relationship.livesWithChild}
            setValue={(val) => { handleValueChange('livesWithChild', val) }}
          />
          <CheckBoxWithLabel label={`Financially responsible for ${childName}`} edge='start' name='financiallyResponsibleFor'
            value={relationship.financiallyResponsibleFor ?? false}
            setValue={(val) => { handleValueChange('financiallyResponsibleFor', val) }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Button data-testid='cancelRelationship' variant='outlined' color='inherit' fullWidth onClick={props.onCancel} sx={buttonSx}>
          Cancel
        </Button>
        {props.onDelete != null && (
          <Button data-testid='deleteRelationship' variant='outlined' color='error' fullWidth onClick={props.onDelete} sx={buttonSx}>
            Delete
          </Button>
        )}
        <Button data-testid='updateRelationship' variant='contained' color='secondary' fullWidth disabled={!showRelationValues || relationship.caregiverID === 0} onClick={handleSubmit} sx={buttonSx}>
          Update
        </Button>
      </Box>
    </Box>
  )
}
